/**
 * eventBonuses — the structured model for Pokémon GO event bonuses.
 *
 * WHAT THIS SOLVES
 * ----------------
 * The live feed (/api/events/live, LeekDuck via ScrapedDuck) states bonuses as
 * raw display strings ("2× Catch Candy") and ONLY for the event shapes the
 * scraper covers (Spotlight Hours, Community Days). Everything else a trainer
 * can actually leverage — event-wide bonuses on generic events, and GO Pass
 * rank perks (which no feed models at all) — has to be curated by hand. This
 * module holds both halves:
 *
 *   1. parseBonusText(): classify a feed string into a BonusKind (+ multiplier)
 *      so surfaces can render a matching icon without each regexing ad hoc
 *      (findNextCandyEvent in myPokemonRecommendations.ts was the precedent).
 *   2. CURATED_EVENT_BONUSES: hand-verified per-event bonus lists and GO Pass
 *      perk tables, keyed by the FEED's eventID so the two sources join for
 *      free. Same auditable-patch-layer shape as megaRosterOverrides.ts:
 *      every entry carries sources + verifiedAt.
 *   3. DAILY_DISCOVERY_BONUSES: the recurring day-of-week bonuses (Friendship
 *      Friday, Max Monday, ...) no feed models, surfaced as synthetic events
 *      spanning the device's LOCAL day via activeDailyBonusEvents().
 *
 * "Active" is decided by the shared recency rule (eventRecency.ts /
 * calendarLinks.ts): event times are floating wall-clock in the trainer's own
 * zone. Do NOT re-derive liveness here.
 */
import { eventStartMs, isEventOver, type RecencyEvent } from "@/lib/eventRecency";
import { decodeEventText } from "@shared/eventText";

/** Icon-level classification of a bonus line. `other` is the safe fallback. */
export type BonusKind =
  | "xp"
  | "stardust"
  | "candy"
  | "hatch"
  | "lucky"
  | "raid"
  | "trade"
  | "incense"
  | "lure"
  | "encounter"
  | "other";

export interface ParsedBonus {
  /** The display string, verbatim (already HTML-decoded by the feed layer). */
  text: string;
  kind: BonusKind;
  /** 2 for "2×"/"double", 0.5 for "1/2 hatch distance"; absent when unstated. */
  multiplier?: number;
}

/**
 * Ordered classifier: first match wins, so the more specific resources come
 * before the mechanics that merely mention them ("2× Raid XP" is an XP bonus
 * delivered by raiding — but "Free Raid Pass" is a raid bonus).
 */
const KIND_RULES: ReadonlyArray<[BonusKind, RegExp]> = [
  ["stardust", /stardust/i],
  ["candy", /cand(y|ies)/i],
  ["xp", /\bxp\b|experience/i],
  ["hatch", /hatch|egg/i],
  ["lucky", /lucky/i],
  ["trade", /trade|trading/i],
  ["raid", /raid/i],
  ["incense", /incense/i],
  ["lure", /lure/i],
  ["encounter", /encounter|photobomb/i],
];

export function parseBonusText(text: string): ParsedBonus {
  const kind = KIND_RULES.find(([, re]) => re.test(text))?.[0] ?? "other";
  let multiplier: number | undefined;
  // No \b after the sign: × is a non-word character, so \b never matches
  // between it and the following space. The lookahead just keeps "2x" in
  // "2xl"-style tokens from counting as a multiplier.
  const times =
    text.match(/(\d+(?:\.\d+)?)\s*[×x](?![a-z0-9])/i) ?? text.match(/[×x]\s*(\d+(?:\.\d+)?)/i);
  if (times) multiplier = Number(times[1]);
  else if (/\bdouble\b/i.test(text)) multiplier = 2;
  else if (/\btriple\b/i.test(text)) multiplier = 3;
  else if (/(?:1\s*\/\s*2|½|half)/i.test(text)) multiplier = 0.5;
  return multiplier ? { text, kind, multiplier } : { text, kind };
}

/* ── GO Pass milestones (curated only — no feed models these) ────────────── */

/**
 * One checkable milestone on a pass (2026-08-27 redesign: the trainer ticks
 * the milestones they have reached, rather than entering a numeric rank —
 * "did I hit Rank 25" is how the pass is actually experienced in-game).
 */
export interface GoPassMilestone {
  /** Stable id for the on-device checklist ("r25"); never reuse across passes. */
  id: string;
  /** Display label ("Rank 25"). */
  label: string;
  /** The perks this milestone grants, one display line each. */
  perks: readonly string[];
  /**
   * Stronger wording when the trainer committed to the Deluxe upgrade —
   * REPLACES `perks` line-for-line (same order). Omit lines that don't change.
   */
  deluxePerks?: readonly string[];
  /** True when the milestone only exists on the Deluxe track. */
  deluxeOnly?: boolean;
}

export interface CuratedGoPass {
  /** Display name for the pass block ("Event GO Pass"). */
  name: string;
  /** Base rank count (bonus ranks continue past it). Omit when unpublished. */
  ranks?: number;
  bonusRanks?: number;
  /**
   * Headline rewards (encounters, items) — the "what you're playing for"
   * strip. Display-only: never counted as active bonuses.
   */
  featured?: readonly string[];
  /** Extra headline rewards on the Deluxe track. */
  deluxeFeatured?: readonly string[];
  /** Sorted by unlock order in the data; keep it that way when appending. */
  milestones: readonly GoPassMilestone[];
}

export interface CuratedEventBonuses {
  /** ScrapedDuck eventID — the join key against /api/events/live. */
  eventId: string;
  /**
   * Event-wide bonus lines, same display-string shape as the feed so both
   * sources render through one pill. Only what a source actually states.
   */
  bonuses?: readonly string[];
  goPass?: CuratedGoPass;
  /** Where each fact came from — keep the layer auditable. */
  sources: readonly string[];
  verifiedAt: string;
}

/**
 * The curated registry. Append an entry when an event ships bonuses the feed
 * does not carry (generic events, GO Fest days, GO Pass perk tables). Entries
 * for finished events can be pruned on sight; nothing reads them once the
 * event leaves the feed's active/upcoming window.
 */
export const CURATED_EVENT_BONUSES: Record<string, CuratedEventBonuses> = {
  "pokemon-xp-2026-worlds": {
    eventId: "pokemon-xp-2026-worlds",
    goPass: {
      name: "Event GO Pass",
      ranks: 30,
      bonusRanks: 30,
      featured: ["Pikachu (Cosmog spacesuit) encounter", "Pikachu (WCS 2026) encounter"],
      milestones: [
        { id: "r10", label: "Rank 10", perks: ["2× catch Stardust"] },
        { id: "r20", label: "Rank 20", perks: ["+3,000 XP for winning raids"] },
        {
          id: "r20-deluxe",
          label: "Rank 20",
          deluxeOnly: true,
          perks: ["Up to 3 free raid passes a day from gym photo discs"],
        },
        { id: "r28", label: "Rank 28", perks: ["Pikachu (Cosmog spacesuit) encounter"] },
        { id: "r29", label: "Rank 29", perks: ["Pikachu (WCS 2026) encounter"] },
        { id: "r59", label: "Rank 59", perks: ["Bonus Pikachu (Cosmog spacesuit) encounter"] },
        { id: "r60", label: "Rank 60", perks: ["Bonus Pikachu (WCS 2026) encounter"] },
      ],
    },
    sources: [
      "https://www.polygon.com/pokemon-go-pokemonxp-2026-worlds-event-pikachu-costume/",
    ],
    verifiedAt: "2026-08-27",
  },
  "go-pass-september-2026": {
    eventId: "go-pass-september-2026",
    goPass: {
      name: "GO Pass: September",
      featured: ["Latios encounter", "Rare Candy XL", "Premium Battle Pass", "Lucky Egg"],
      deluxeFeatured: ["Timed Incubator", "Super Incubator", "Additional encounters"],
      milestones: [
        {
          id: "r1",
          label: "Rank 1",
          perks: [
            "One additional Candy for trading Pokémon",
            "Guaranteed Candy XL when trading (level 31+)",
          ],
        },
        {
          id: "r20-deluxe",
          label: "Rank 20",
          deluxeOnly: true,
          perks: ["Timed Incubator (lasts until a week after the pass ends)"],
        },
        {
          id: "r25",
          label: "Rank 25",
          perks: [
            "Open up to 40 Gifts per day",
            "Receive up to 125 Gifts per day from photo discs",
            "Hold 10 more Gifts in your Item Bag",
          ],
          deluxePerks: [
            "Open up to 50 Gifts per day",
            "Receive up to 150 Gifts per day from photo discs",
            "Hold 20 more Gifts in your Item Bag",
          ],
        },
        { id: "r50", label: "Rank 50", perks: ["2× Daily Adventure Incense duration"] },
        {
          id: "r75",
          label: "Rank 75",
          perks: ["Increased XP and Stardust from hatching Eggs"],
        },
      ],
    },
    sources: ["https://leekduck.com/events/go-pass-september-2026/"],
    verifiedAt: "2026-08-27",
  },
};

/* ── Daily Discovery (recurring day-of-week bonuses) ─────────────────────── */

/**
 * The weekly Daily Discovery schedule: recurring bonuses Pokémon GO grants on
 * a fixed day of the week (Friendship Friday, Max Monday, ...), active from
 * 12:00 a.m. to 11:59 p.m. LOCAL time on that day. No feed models these, so
 * they are curated here, same auditable shape as CURATED_EVENT_BONUSES
 * (sources + verifiedAt). Revisit each season: Niantic rotates the lineup
 * (Double-Time Sunday became Scenic Sunday for Forever Forward).
 */
export interface DailyDiscoveryBonus {
  /** Synthetic event id ("daily-friendship-friday"); stable across weeks. */
  id: string;
  /** Display name, used as the event name wherever bonuses are grouped. */
  name: string;
  /** Local device weekday, Date#getDay convention: 0 Sunday .. 6 Saturday. */
  weekday: number;
  /** Bonus lines, same display-string shape the feed uses. */
  bonuses: readonly string[];
  sources: readonly string[];
  verifiedAt: string;
}

export const DAILY_DISCOVERY_BONUSES: readonly DailyDiscoveryBonus[] = [
  {
    id: "daily-scenic-sunday",
    name: "Scenic Sunday",
    weekday: 0,
    bonuses: [
      "More Pokémon appear while following a Route",
      "Incense attracts even more Pokémon on Routes",
      "Reduced distance to earn Buddy Candy on Routes",
    ],
    sources: ["https://leekduck.com/events/season-23-forever-forward/"],
    verifiedAt: "2026-08-28",
  },
  {
    id: "daily-max-monday",
    name: "Max Monday",
    weekday: 1,
    bonuses: [
      "One Rare Candy XL for completing in-person Max Battles",
      "More Power Spots are active, and they refresh more often",
    ],
    sources: ["https://leekduck.com/events/season-23-forever-forward/"],
    verifiedAt: "2026-08-28",
  },
  {
    id: "daily-showcase-tuesday",
    name: "Showcase Tuesday",
    weekday: 2,
    bonuses: ["Enter up to five PokéStop Showcases"],
    sources: ["https://leekduck.com/events/season-23-forever-forward/"],
    verifiedAt: "2026-08-28",
  },
  {
    id: "daily-raid-wednesday",
    name: "Raid Wednesday",
    weekday: 3,
    bonuses: ["One Rare Candy XL for completing in-person Raid Battles"],
    sources: ["https://leekduck.com/events/season-23-forever-forward/"],
    verifiedAt: "2026-08-28",
  },
  {
    id: "daily-go-battle-thursday",
    name: "GO Battle Thursday",
    weekday: 4,
    bonuses: [
      "Up to 4× Stardust from GO Battle League win rewards",
      "Play up to 10 GO Battle League sets (50 battles)",
    ],
    sources: ["https://leekduck.com/events/season-23-forever-forward/"],
    verifiedAt: "2026-08-28",
  },
  {
    id: "daily-friendship-friday",
    name: "Friendship Friday",
    weekday: 5,
    bonuses: [
      "Up to two additional Special Trades",
      "Trades require 20% less Stardust",
      "Increased chance of Lucky Trades",
      "Two guaranteed Candy XL from trades (level 31 and up)",
    ],
    sources: ["https://leekduck.com/events/season-23-forever-forward/"],
    verifiedAt: "2026-08-28",
  },
  // Saturday deliberately has no entry: the current season grants no
  // Saturday-wide Daily Discovery bonus.
];

/**
 * Weeks Niantic switches the Daily Discovery schedule OFF. Big events replace
 * the seasonal rhythm outright ("Daily Discoveries, Seasonal Mega Raids, ...
 * will not take place during the Mega Ascension event and Pokémon GO Fest
 * 2026: Mega Finale"), and showing Friendship Friday in a week with no trade
 * discount misleads (2026-09-04 owner report). Dates are LOCAL calendar days,
 * inclusive on both ends, so the pause lifts at the trainer's own midnight
 * the same way the daily lineup flips. Entries stay in the file after they
 * lapse: they document a week's history and cost nothing.
 */
export interface DailyDiscoverySuspension {
  /** First local day with no Daily Discovery (YYYY-MM-DD). */
  from: string;
  /** Last local day with no Daily Discovery (YYYY-MM-DD, inclusive). */
  until: string;
  /** What replaced the schedule, for the audit trail. */
  reason: string;
  sources: readonly string[];
  verifiedAt: string;
}

export const DAILY_DISCOVERY_SUSPENSIONS: readonly DailyDiscoverySuspension[] = [
  {
    from: "2026-08-31",
    until: "2026-09-06",
    reason: "Mega Ascension + GO Fest 2026: Mega Finale week",
    sources: ["https://leekduck.com/events/pokemon-go-fest-2026-mega-finale/"],
    verifiedAt: "2026-09-04",
  },
];

/** Local calendar day of `now`, as the YYYY-MM-DD the suspensions use. */
function localDayKey(now: number): string {
  const d = new Date(now);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * The suspension covering the device's local day, or null when the normal
 * schedule applies. String compare is safe: the keys are zero-padded ISO
 * dates, so lexical order is calendar order.
 */
export function dailyDiscoverySuspension(
  now: number = Date.now(),
): DailyDiscoverySuspension | null {
  const day = localDayKey(now);
  return DAILY_DISCOVERY_SUSPENSIONS.find((s) => s.from <= day && day <= s.until) ?? null;
}

/** A Daily Discovery rendered as a synthetic feed-shaped event. */
export type DailyBonusEvent = BonusFeedEvent & {
  name: string;
  start: string;
  end: string;
  bonuses: string[];
};

/**
 * The Daily Discovery bonuses live TODAY, as synthetic events spanning the
 * device's local day (floating "YYYY-MM-DDTHH:mm:ss" strings, which
 * eventCountdownMs reads as local wall clock). Because the day is derived from
 * the device clock, the lineup flips automatically at local midnight:
 * Friendship Friday hands over to Saturday's quiet day exactly when the
 * trainer's own Friday ends. Callers concat these with the feed's events
 * before collectActiveBonuses so every surface (the Bonus bubble, the Bonuses
 * page, the Calendar) counts the same set. Empty during a
 * DAILY_DISCOVERY_SUSPENSIONS week.
 */
export function activeDailyBonusEvents(now: number = Date.now()): DailyBonusEvent[] {
  // A suspended week has NO daily lineup: the bubble, the Bonuses page and
  // the Calendar all read this one function, so they go quiet together.
  if (dailyDiscoverySuspension(now)) return [];
  const d = new Date(now);
  const day = localDayKey(now);
  return DAILY_DISCOVERY_BONUSES.filter((b) => b.weekday === d.getDay()).map((b) => ({
    id: b.id,
    name: b.name,
    start: `${day}T00:00:00`,
    end: `${day}T23:59:59`,
    bonuses: [...b.bonuses],
  }));
}

/* ── Merging + liveness ──────────────────────────────────────────────────── */

/** The slice of a live-feed event this module needs. */
export interface BonusFeedEvent extends RecencyEvent {
  id: string;
  name?: string;
  bonuses?: string[];
  /**
   * A GO Pass delivered by the feed itself: /api/events/live overlays the
   * Worker's cron-extracted event_bonuses rows (LeekDuck rank tables reduced
   * to this same milestone model), so passes arrive without a client deploy.
   * The curated layer below still wins when both exist.
   */
  goPass?: CuratedGoPass;
}

/**
 * Feed bonuses ∪ curated bonuses for one event, deduped case-insensitively
 * (the feed sometimes restates a line the curator already added), parsed.
 */
export function mergedBonuses(ev: Pick<BonusFeedEvent, "id" | "bonuses">): ParsedBonus[] {
  const curated = CURATED_EVENT_BONUSES[ev.id]?.bonuses ?? [];
  const seen = new Set<string>();
  const out: ParsedBonus[] = [];
  for (const raw of [...(ev.bonuses ?? []), ...curated]) {
    // Feed strings are scraped HTML: decode entities here (idempotent), same
    // belt-and-braces as liveToGameEvent does for titles.
    const text = decodeEventText(raw);
    const key = text.trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(parseBonusText(text));
  }
  return out;
}

/**
 * The GO Pass for an event, from either source: the hand-curated registry
 * wins (a human verified it against the announcement), the feed's payload
 * pass covers everything the curator never touched.
 */
export function goPassFor(ev: Pick<BonusFeedEvent, "id" | "goPass">): CuratedGoPass | undefined {
  const pass = CURATED_EVENT_BONUSES[ev.id]?.goPass ?? ev.goPass;
  return pass ? normalizeGoPass(pass) : undefined;
}

/**
 * Fold milestones that share an id into one, unioning their perk lines.
 *
 * 2026-09-01 device report: Mega Ascension's Right now list showed "Link Charge
 * cap increase to 2,000", "Shiny Raid Bosses from four-star Mega Raids" and the
 * Mega Raid candy line THREE TIMES each. Its LeekDuck page carries one rank
 * table per starter track (Chespin, Fennekin, Froakie), and the extractor walks
 * every `rank-item` on the page in order, so Rank 1 arrived three times under
 * the same milestone id — once per table, each repeating the perks the tracks
 * share and differing only in the starter.
 *
 * Duplicate ids are worse than duplicate rows: the on-device checklist is keyed
 * by id, so ticking "Rank 1" ticked all three at once and every copy's perks
 * fired. Folding here, at the one chokepoint every pass reader goes through,
 * fixes the checklist, the Right now list and the bubble's count together — and
 * fixes them for the passes ALREADY stored on device, which a worker-side fix
 * alone would not (the refresh only re-extracts when the page's hash moves).
 *
 * Perks fold as (perk, deluxePerk) PAIRS so the line-for-line Deluxe
 * replacement survives the merge, deduped on the perk line case-insensitively,
 * in first-appearance order. So Rank 1 above becomes one milestone granting the
 * Link Charge cap plus each of the three starter Shiny lines: nothing repeated,
 * nothing dropped.
 */
export function normalizeGoPass(pass: CuratedGoPass): CuratedGoPass {
  const byId = new Map<string, GoPassMilestone>();
  const pairsById = new Map<string, { seen: Set<string>; perks: string[]; deluxe: string[] }>();
  const order: string[] = [];
  let collapsed = false;

  for (const m of pass.milestones) {
    let acc = pairsById.get(m.id);
    if (!acc) {
      acc = { seen: new Set<string>(), perks: [], deluxe: [] };
      pairsById.set(m.id, acc);
      byId.set(m.id, m);
      order.push(m.id);
    } else {
      collapsed = true;
    }
    m.perks.forEach((perk, i) => {
      const key = perk.trim().toLowerCase();
      if (!key || acc!.seen.has(key)) return;
      acc!.seen.add(key);
      acc!.perks.push(perk);
      acc!.deluxe.push(m.deluxePerks?.[i] ?? perk);
    });
  }

  if (!collapsed) return pass;

  const milestones = order.map((id) => {
    const first = byId.get(id)!;
    const acc = pairsById.get(id)!;
    // deluxePerks only survive if at least one contributing milestone had them:
    // otherwise the padded copy would claim a Deluxe track the pass never had.
    const hasDeluxe = acc.deluxe.some((line, i) => line !== acc.perks[i]);
    return {
      ...first,
      perks: acc.perks,
      ...(hasDeluxe ? { deluxePerks: acc.deluxe } : {}),
    };
  });
  return { ...pass, milestones };
}

/**
 * Curated-registry passes only, for callers that hold nothing but an eventId.
 * The one real consumer is goPassRankStore's legacy-rank migration: numeric
 * rank data predates feed-delivered passes, so it can only ever reference a
 * curated pass.
 */
export function curatedGoPassFor(eventId: string): CuratedGoPass | undefined {
  return CURATED_EVENT_BONUSES[eventId]?.goPass;
}

/** On-device pass progress: which milestones are ticked + Deluxe commitment. */
export interface GoPassProgress {
  /** Milestone ids the trainer has marked reached. */
  checked: readonly string[];
  /** True when the trainer committed to the paid Deluxe upgrade. */
  deluxe: boolean;
}

/** The milestones a trainer can see: Deluxe-only rows need the commitment. */
export function visibleGoPassMilestones(
  pass: CuratedGoPass,
  deluxe: boolean,
): GoPassMilestone[] {
  return pass.milestones.filter((m) => !m.deluxeOnly || deluxe);
}

/**
 * The perk lines a milestone grants at the trainer's commitment level:
 * deluxePerks replace perks line-for-line when Deluxe is committed.
 */
export function milestonePerkLines(m: GoPassMilestone, deluxe: boolean): readonly string[] {
  if (!deluxe || !m.deluxePerks) return m.perks;
  return m.perks.map((line, i) => m.deluxePerks?.[i] ?? line);
}

/** One perk line from a ticked milestone, tagged with where it came from. */
export interface AchievedGoPassPerk {
  milestone: GoPassMilestone;
  text: string;
}

/** Perk lines from the milestones the trainer has ticked (Deluxe-aware). */
export function achievedGoPassPerks(
  pass: CuratedGoPass,
  progress: GoPassProgress,
): AchievedGoPassPerk[] {
  const checked = new Set(progress.checked);
  const out: AchievedGoPassPerk[] = [];
  for (const m of visibleGoPassMilestones(pass, progress.deluxe)) {
    if (!checked.has(m.id)) continue;
    for (const text of milestonePerkLines(m, progress.deluxe)) out.push({ milestone: m, text });
  }
  return out;
}

/** True when the event has started and is not over, on the device clock. */
export function isEventActive(ev: RecencyEvent, now: number = Date.now()): boolean {
  const startMs = eventStartMs(ev);
  if (!Number.isFinite(startMs) || startMs > now) return false;
  return !isEventOver(ev, now);
}

/** One bonus a trainer can leverage right now, tagged with its event. */
export interface ActiveBonus {
  eventId: string;
  eventName: string;
  bonus: ParsedBonus;
  /** Set when the bonus is a GO Pass perk: the milestone label ("Rank 25"). */
  goPassMilestone?: string;
}

/** The bonuses live right now for ONE event, for grouped rendering. */
export interface ActiveBonusGroup {
  eventId: string;
  eventName: string;
  bonuses: ActiveBonus[];
}

/**
 * Bundle collectActiveBonuses output by the event each bonus applies from, in
 * first-appearance order, so the "Right now" list can put the event's name
 * above its lines ("Friendship Friday" and then what it grants) instead of
 * repeating the attribution under every line.
 */
export function groupActiveBonuses(bonuses: readonly ActiveBonus[]): ActiveBonusGroup[] {
  const byEvent = new Map<string, ActiveBonusGroup>();
  const out: ActiveBonusGroup[] = [];
  for (const b of bonuses) {
    let group = byEvent.get(b.eventId);
    if (!group) {
      group = { eventId: b.eventId, eventName: b.eventName, bonuses: [] };
      byEvent.set(b.eventId, group);
      out.push(group);
    }
    group.bonuses.push(b);
  }
  return out;
}

/**
 * Every bonus live right now across the feed's events: event-wide bonuses of
 * active events, plus GO Pass perks from the milestones the trainer has ticked
 * (progressFor returns their checklist + Deluxe flag per event, from
 * goPassRankStore). This feeds BOTH the floating Bonus bubble's count and the
 * Bonuses page "Right now" summary, so the two can never disagree.
 */
export function collectActiveBonuses(
  events: readonly BonusFeedEvent[],
  progressFor: (eventId: string) => GoPassProgress,
  now: number = Date.now(),
): ActiveBonus[] {
  const out: ActiveBonus[] = [];
  for (const ev of events) {
    if (!isEventActive(ev, now)) continue;
    const name = ev.name ?? ev.id;
    // One line per bonus PER EVENT. mergedBonuses already dedupes the
    // event-wide lines against each other; this also stops a GO Pass perk
    // restating a line the event grants outright, which is how the same
    // sentence reached the list twice with only a "Rank 1" tag to tell them
    // apart. Two DIFFERENT events granting the same bonus still both show —
    // the attribution above each block is the point of the list.
    const seen = new Set<string>();
    const push = (b: ActiveBonus): void => {
      const key = b.bonus.text.trim().toLowerCase();
      if (!key || seen.has(key)) return;
      seen.add(key);
      out.push(b);
    };
    for (const bonus of mergedBonuses(ev)) {
      push({ eventId: ev.id, eventName: name, bonus });
    }
    const pass = goPassFor(ev);
    if (pass) {
      for (const perk of achievedGoPassPerks(pass, progressFor(ev.id))) {
        push({
          eventId: ev.id,
          eventName: name,
          bonus: parseBonusText(perk.text),
          goPassMilestone: perk.milestone.label,
        });
      }
    }
  }
  return out;
}
